import { useLocalObservable } from 'mobx-react-lite';
import { useEffect } from 'react';

import { IImageLoadHandlerParams } from '@gem/components';

import Tasks from 'APP/Tasks';
import { ImageCropperPopup } from 'APP/main/PopupManager/Popups/ImageCropperPopup/ImageCropperPopup';

import {
  GroupFormStep,
  IGroupFormInitialValues,
  TFormSubmitHandler,
  TFormUpdateHandler,
  TSubmitHandler,
} from './GroupForm.types';

interface IGroupFormPresenterParams {
  initialValues: IGroupFormInitialValues;
  isShowMembers: boolean;
  onSubmit: TSubmitHandler;
}

interface IGroupFormPresenter {
  step: GroupFormStep;
  membersIds: string[];
  groupData: IGroupFormInitialValues;
  initialValues: IGroupFormInitialValues;
  showMembers: boolean;
  isMembers: boolean;
  isGeneralInfo: boolean;
  isShowMembers: boolean;
  isShowGeneralInfo: boolean;
  isShowPrevStep: boolean;
  setShowMembers(show: boolean): void;
  onSetMembersIds(membersIds: string[]): void;
  onShowMembers(): void;
  onShowGeneralInfo(): void;
  onUpdateGroupData: TFormUpdateHandler;
  onSubmit: TFormSubmitHandler;
  onImageLoad(params: IImageLoadHandlerParams): void;
}

export const useGroupFormPresenter = (data: IGroupFormPresenterParams): IGroupFormPresenter => {
  const { initialValues, isShowMembers, onSubmit } = data;

  const presenter = useLocalObservable<IGroupFormPresenter>(() => ({
    step: isShowMembers ? GroupFormStep.Members : GroupFormStep.GeneralInfo,
    membersIds: [],
    groupData: initialValues,
    initialValues,
    showMembers: isShowMembers,

    get isMembers(): boolean {
      return presenter.step === GroupFormStep.Members;
    },

    get isGeneralInfo(): boolean {
      return presenter.step === GroupFormStep.GeneralInfo;
    },

    get isShowMembers(): boolean {
      return presenter.showMembers && presenter.isMembers;
    },

    get isShowGeneralInfo(): boolean {
      return !presenter.showMembers || presenter.isGeneralInfo;
    },

    get isShowPrevStep(): boolean {
      return presenter.showMembers && presenter.isShowGeneralInfo;
    },

    setShowMembers(show: boolean): void {
      presenter.showMembers = show;
    },

    onSetMembersIds(membersIds: string[]): void {
      presenter.membersIds = membersIds;
    },

    onShowMembers(): void {
      presenter.step = GroupFormStep.Members;
    },

    onShowGeneralInfo(): void {
      presenter.step = GroupFormStep.GeneralInfo;
    },

    onUpdateGroupData({ values }): void {
      presenter.groupData = values;
    },

    async onSubmit(groupData: IGroupFormInitialValues): Promise<void> {
      await onSubmit({
        ...groupData,
        members: [...presenter.membersIds],
      });
    },

    onImageLoad(params: IImageLoadHandlerParams): void {
      Tasks.app.openPopup(ImageCropperPopup, { params });
    },
  }));

  useEffect(() => {
    presenter.groupData = initialValues;
  }, [initialValues]);

  useEffect(() => {
    presenter.setShowMembers(isShowMembers);
  }, [isShowMembers]);

  return presenter;
};
